import React from 'react';
import { TypeSectionBanner } from "../../@types/sectionTypes";
import { BANNER_TYPE } from "../../../globals/constants";
import SectionInnerPageBanner from "../../sections/common/banner";
import PolicyLayout from "../../components/PolicyLayout";

const DataBreachPolicyPage = () => {
    const bannerData: TypeSectionBanner = {
        title: "Data Breach Notification Policy",
        crumb: "Data Breach Policy",
        image: "/assets/images/banner/1.jpg"
    };

    const sections = [
        { id: "introduction", title: "Introduction" },
        { id: "scope", title: "Scope and Application" },
        { id: "definitions", title: "Definitions" },
        { id: "notification-procedures", title: "Notification Procedures" },
        { id: "internal-response", title: "Internal Response Team" },
        { id: "assessment", title: "Breach Assessment" },
        { id: "client-notification", title: "Client Notification" },
        { id: "regulatory-reporting", title: "Regulatory Reporting" },
        { id: "documentation", title: "Documentation Requirements" },
        { id: "training", title: "Training and Awareness" },
        { id: "contact", title: "Contact Information" }
    ];

    return (
        <div className="data-breach-policy-page">
            <SectionInnerPageBanner _data={bannerData} _type={BANNER_TYPE.COMPACT} />
            
            <PolicyLayout title="Data Breach Notification Policy" sections={sections}>

                <div id="introduction" className="section">
                    <h2>1. Introduction</h2>
                    <p>
                        This Data Breach Notification Policy establishes procedures for responding to data security incidents 
                        at Anathoth Technologies Ltd. Our commitment to protecting client data requires a systematic approach 
                        to incident detection, assessment, containment, and notification.
                    </p>
                    <p>
                        This policy ensures compliance with applicable data protection regulations including Uganda's Data 
                        Protection and Privacy Act, GDPR, and industry best practices for technology service providers.
                    </p>
                </div>

                <div id="scope" className="section">
                    <h2>2. Scope and Application</h2>
                    <p>This policy applies to:</p>
                    <ul>
                        <li>All Anathoth Technologies Ltd employees, contractors, and third-party vendors</li>
                        <li>All systems, applications, and infrastructure managed by Anathoth</li>
                        <li>All client data processed, stored, or transmitted by our services</li>
                        <li>Custom software development projects and platforms</li>
                        <li>Mobile applications and web platforms we develop or maintain</li>
                        <li>Payment gateway integrations and financial data processing</li>
                        <li>Cloud services and hosting environments under our management</li>
                        <li>IT consulting and technical support activities</li>
                    </ul>
                </div>

                <div id="definitions" className="section">
                    <h2>3. Definitions</h2>
                    <ul>
                        <li><strong>Personal Data:</strong> Any information relating to an identified or identifiable natural person</li>
                        <li><strong>Data Breach:</strong> A security incident that results in the accidental or unlawful destruction, loss, alteration, unauthorized disclosure of, or access to personal data</li>
                        <li><strong>Sensitive Data:</strong> Personal data revealing racial or ethnic origin, political opinions, religious beliefs, trade union membership, genetic data, biometric data, health data, or data concerning sex life</li>
                        <li><strong>Security Incident:</strong> Any event that could compromise the confidentiality, integrity, or availability of data or systems</li>
                        <li><strong>Data Controller:</strong> Our clients who determine the purposes and means of processing personal data</li>
                        <li><strong>Data Processor:</strong> Anathoth Technologies Ltd when processing personal data on behalf of clients</li>
                        <li><strong>Business Data:</strong> Proprietary information including source code, business processes, client lists, and technical specifications</li>
                    </ul>
                </div>

                <div id="notification-procedures" className="section">
                    <h2>4. Notification Procedures</h2>
                    
                    <h3>4.1 Immediate Actions (Within 1 Hour)</h3>
                    <ul>
                        <li>Any employee discovering a potential data breach must immediately notify their supervisor and the IT Security Team</li>
                        <li>Contact the Chief Technology Officer at +256 744 666608 or samuel.kawalya@theanathoth.com</li>
                        <li>If the CTO is unavailable, contact the CEO at ezer.masaba@theanathoth.com</li>
                        <li>Do not discuss the incident with unauthorized personnel</li>
                        <li>Preserve all evidence and logs related to the incident</li>
                        <li>Document the time of discovery and initial observations</li>
                    </ul>

                    <h3>4.2 Initial Assessment (Within 4 Hours)</h3>
                    <ul>
                        <li>Convene the Data Breach Response Team</li>
                        <li>Conduct preliminary assessment of the incident scope</li>
                        <li>Implement immediate containment measures</li>
                        <li>Identify affected systems and data types</li>
                        <li>Document all actions taken in the incident log</li>
                        <li>Determine if law enforcement involvement is necessary</li>
                    </ul>
                </div>

                <div id="internal-response" className="section">
                    <h2>5. Internal Response Team</h2>
                    
                    <h3>5.1 Data Breach Response Team</h3>
                    <ul>
                        <li><strong>Incident Commander:</strong> Chief Technology Officer (Samuel Kawalya)</li>
                        <li><strong>Legal Advisor:</strong> External legal counsel specializing in data protection</li>
                        <li><strong>IT Security Lead:</strong> Senior technical team member</li>
                        <li><strong>Communications Lead:</strong> COO (Vivian Agenorwot)</li>
                        <li><strong>Executive Sponsor:</strong> CEO (Ezer Masaba)</li>
                        <li><strong>HR Representative:</strong> Karen Lusimbo (for employee-related incidents)</li>
                    </ul>

                    <h3>5.2 Team Responsibilities</h3>
                    <ul>
                        <li><strong>Incident Commander:</strong> Overall incident coordination and technical response</li>
                        <li><strong>Legal Advisor:</strong> Regulatory compliance and legal implications assessment</li>
                        <li><strong>IT Security Lead:</strong> Technical investigation and remediation</li>
                        <li><strong>Communications Lead:</strong> Client communications and stakeholder management</li>
                        <li><strong>Executive Sponsor:</strong> Strategic decisions and regulatory notifications</li>
                        <li><strong>HR Representative:</strong> Employee support and internal communications</li>
                    </ul>
                </div>

                <div id="assessment" className="section">
                    <h2>6. Breach Assessment</h2>
                    
                    <h3>6.1 Risk Assessment Criteria</h3>
                    <p>The response team will assess each incident based on:</p>
                    <ul>
                        <li><strong>Data Type:</strong> Personal, sensitive, financial, business confidential, or technical data</li>
                        <li><strong>Volume:</strong> Number of affected individuals or records</li>
                        <li><strong>Impact:</strong> Potential harm to affected individuals and clients</li>
                        <li><strong>Likelihood of Misuse:</strong> Probability that data will be misused</li>
                        <li><strong>Client Impact:</strong> Effect on client operations, reputation, and compliance</li>
                        <li><strong>Technical Severity:</strong> System compromise level and attack sophistication</li>
                        <li><strong>Business Impact:</strong> Effect on ongoing projects and service delivery</li>
                    </ul>

                    <h3>6.2 Classification Levels</h3>
                    <ul>
                        <li><strong>Low Risk:</strong> Minimal or no harm expected; limited scope (e.g., single user account compromise)</li>
                        <li><strong>Medium Risk:</strong> Potential for some harm; moderate scope (e.g., non-sensitive business data exposure)</li>
                        <li><strong>High Risk:</strong> Significant potential harm; broad scope or sensitive data (e.g., client database access)</li>
                        <li><strong>Critical Risk:</strong> Severe harm likely; extensive scope, financial data, or system compromise</li>
                    </ul>
                </div>

                <div id="client-notification" className="section">
                    <h2>7. Client Notification</h2>
                    
                    <h3>7.1 Notification Timeline</h3>
                    <ul>
                        <li><strong>Critical Risk:</strong> Within 12 hours of confirmation</li>
                        <li><strong>High Risk:</strong> Within 24 hours of confirmation</li>
                        <li><strong>Medium Risk:</strong> Within 72 hours of confirmation</li>
                        <li><strong>Low Risk:</strong> Within 7 days or as contractually required</li>
                    </ul>

                    <h3>7.2 Notification Content</h3>
                    <p>Client notifications will include:</p>
                    <ul>
                        <li>Clear description of the incident and timeline of events</li>
                        <li>Types of data involved and potential exposure</li>
                        <li>Number of affected individuals (if known)</li>
                        <li>Immediate actions taken to address the breach</li>
                        <li>Ongoing mitigation and remediation measures</li>
                        <li>Recommendations for client actions to protect affected individuals</li>
                        <li>Contact information for questions and support</li>
                        <li>Schedule for regular updates on investigation progress</li>
                        <li>Available resources for affected individuals</li>
                    </ul>

                    <h3>7.3 Communication Methods</h3>
                    <ul>
                        <li>Primary: Secure email to designated client contacts</li>
                        <li>Secondary: Encrypted messaging platforms (Signal, Wire)</li>
                        <li>Critical incidents: Direct phone calls followed by written confirmation</li>
                        <li>Formal follow-up: Official incident report via secure channels</li>
                    </ul>
                </div>

                <div id="regulatory-reporting" className="section">
                    <h2>8. Regulatory Reporting</h2>
                    
                    <h3>8.1 Uganda Data Protection Office</h3>
                    <ul>
                        <li>Report within 72 hours of becoming aware of the breach</li>
                        <li>Submit detailed incident report including remediation measures</li>
                        <li>Provide updates as investigation progresses</li>
                        <li>Submit final incident report with lessons learned</li>
                    </ul>

                    <h3>8.2 International Compliance</h3>
                    <ul>
                        <li><strong>GDPR:</strong> Notify relevant supervisory authorities within 72 hours if EU data is affected</li>
                        <li><strong>Client Jurisdictions:</strong> Support client reporting obligations in their respective countries</li>
                        <li><strong>Sector-Specific Regulators:</strong> Report to financial or industry regulators as required</li>
                        <li><strong>Cross-Border Notifications:</strong> Coordinate with international data protection authorities</li>
                    </ul>

                    <h3>8.3 Documentation Requirements</h3>
                    <ul>
                        <li>Maintain detailed logs of all notifications sent</li>
                        <li>Record responses from regulatory authorities</li>
                        <li>Document any required follow-up actions</li>
                        <li>Preserve evidence for potential legal proceedings</li>
                    </ul>
                </div>

                <div id="documentation" className="section">
                    <h2>9. Documentation Requirements</h2>
                    
                    <h3>9.1 Incident Documentation</h3>
                    <p>All incidents must be documented with:</p>
                    <ul>
                        <li>Date and time of discovery and initial response</li>
                        <li>Detailed description of the incident and root cause</li>
                        <li>Data types and volumes affected</li>
                        <li>Affected systems, applications, and infrastructure</li>
                        <li>Complete timeline of events and response actions</li>
                        <li>Actions taken for containment and remediation</li>
                        <li>Notifications sent and recipients</li>
                        <li>Regulatory and legal consultations</li>
                        <li>Lessons learned and improvements implemented</li>
                        <li>Final impact assessment and closure report</li>
                    </ul>

                    <h3>9.2 Record Retention</h3>
                    <ul>
                        <li>Incident records: Retained for 7 years</li>
                        <li>Investigation files: Retained for 5 years</li>
                        <li>Communication logs: Retained for 3 years</li>
                        <li>Remediation evidence: Retained for 3 years</li>
                        <li>Training records: Retained for 3 years</li>
                    </ul>
                </div>

                <div id="training" className="section">
                    <h2>10. Training and Awareness</h2>
                    
                    <h3>10.1 Employee Training</h3>
                    <ul>
                        <li>Annual data breach response training for all employees</li>
                        <li>Specialized training for response team members</li>
                        <li>Quarterly incident simulation exercises</li>
                        <li>Security awareness programs covering breach prevention</li>
                        <li>Role-specific training for developers and system administrators</li>
                    </ul>

                    <h3>10.2 Training Content</h3>
                    <ul>
                        <li>How to identify potential security incidents</li>
                        <li>Proper incident reporting procedures and timelines</li>
                        <li>Evidence preservation techniques</li>
                        <li>Communication protocols during incidents</li>
                        <li>Legal and regulatory requirements</li>
                        <li>Client notification procedures</li>
                        <li>Technical response and remediation procedures</li>
                    </ul>

                    <h3>10.3 Awareness Programs</h3>
                    <ul>
                        <li>Monthly security bulletins highlighting emerging threats</li>
                        <li>Regular briefings on lessons learned from incidents</li>
                        <li>Industry best practice sharing sessions</li>
                        <li>Client-specific security briefings</li>
                    </ul>
                </div>

                <div id="contact" className="section">
                    <h2>11. Contact Information</h2>
                    
                    <h3>Emergency Contacts (24/7)</h3>
                    <ul>
                        <li><strong>Primary Contact:</strong> Chief Technology Officer - Samuel Kawalya</li>
                        <li><strong>Email:</strong> security@theanathoth.com</li>
                        <li><strong>Phone:</strong> +256 744 666608</li>
                        <li><strong>Secondary Contact:</strong> CEO - Ezer Masaba</li>
                        <li><strong>Email:</strong> info@theanathoth.com</li>
                    </ul>

                    <h3>Business Hours Support</h3>
                    <ul>
                        <li><strong>General inquiries:</strong> info@theanathoth.com</li>
                        <li><strong>Technical support:</strong> support@theanathoth.com</li>
                        <li><strong>Legal matters:</strong> legal@theanathoth.com</li>
                        <li><strong>Office hours:</strong> Monday-Friday, 8 AM - 6 PM EAT</li>
                        <li><strong>Office address:</strong> 1 Kololo Hill Drive, Kampala, Uganda</li>
                    </ul>

                    <p><em>Last updated on 01 January, 2025</em></p>
                </div>
            </PolicyLayout>
        </div>
    );
};

export default DataBreachPolicyPage; 